import axiosClient from './axiosClient';

export interface User {
  id: number;
  name: string;
  email: string;
  company_id: number | null;
  department_id: number | null;
  is_active: boolean;
  role_id: number;
  created_at: string;
  role_name: string;
  company_name: string;
  department_name: string;
}

export interface CreateUserPayload {
  name: string;
  email: string;
  company_id: number;
  department_id: number;
  password: string;
  role_id: number;
}

export interface UpdateUserPayload {
  name?: string;
  email?: string;
  company_id?: number;
  department_id?: number;
  role_id?: number;
  is_active?: boolean;
}

export interface GetUsersParams {
  skip?: number;
  limit?: number;
  company_id?: number;
  department_id?: number;
  role_id?: number;
}

// --------------------------------------------------
// USER API FUNCTIONS
// --------------------------------------------------

// Get all users with pagination and filters
export const getUsers = async (params?: GetUsersParams): Promise<User[]> => {
  const response = await axiosClient.get('/users/', {
    params: {
      skip: params?.skip || 0,
      limit: params?.limit || 100,
      company_id: params?.company_id,
      department_id: params?.department_id,
      role_id: params?.role_id
    }
  });
  return response.data;
};

// Get current user info
export const getCurrentUser = async (): Promise<User> => {
  const response = await axiosClient.get('/users/me');
  return response.data;
};

// Get user by ID
export const getUserById = async (userId: number): Promise<User> => {
  const response = await axiosClient.get(`/users/${userId}`);
  return response.data;
};

// Create new user
export const createUser = async (
  userData: CreateUserPayload
): Promise<User> => {
  const response = await axiosClient.post('/users/', userData);
  return response.data;
};

// Update user
export const updateUser = async (
  userId: number,
  userData: UpdateUserPayload
): Promise<User> => {
  const response = await axiosClient.put(`/users/${userId}`, userData);
  return response.data;
};

// Update current user
export const updateCurrentUser = async (
  userData: UpdateUserPayload
): Promise<User> => {
  const response = await axiosClient.put('/users/me', userData);
  return response.data;
};

// Delete user
export const deleteUser = async (userId: number): Promise<string> => {
  const response = await axiosClient.delete(`/users/${userId}`);
  return response.data;
};

// --------------------------------------------------
// RELATED ENTITY APIs (FOR DROPDOWNS)
// --------------------------------------------------

export interface Company {
  id: number;
  name: string;
  is_active: boolean;
  created_at: string;
}

export interface Department {
  id: number;
  name: string;
  company_id: number;
  is_active: boolean;
  created_at: string;
  company_name?: string;
}

export interface Role {
  id: number;
  name: string;
  permissions: string[];
  is_active: boolean;
  created_at: string;
}

// Get companies for dropdown
export const getCompanies = async (): Promise<Company[]> => {
  const response = await axiosClient.get('/companies/', {
    params: { skip: 0, limit: 1000 }
  });
  return response.data;
};

// Get departments (optionally filtered by company)
export const getDepartments = async (
  companyId?: number
): Promise<Department[]> => {
  const params: { skip: number; limit: number; company_id?: number } = {
    skip: 0,
    limit: 1000
  };

  if (companyId) {
    params.company_id = companyId;
  }

  const response = await axiosClient.get('/departments/', { params });
  return response.data;
};

// Get roles for dropdown
export const getRoles = async (): Promise<Role[]> => {
  const response = await axiosClient.get('/roles/', {
    params: { skip: 0, limit: 1000 }
  });
  return response.data;
};

// --------------------------------------------------
// IMPROVED FILTER FUNCTIONS (EFFICIENT)
// --------------------------------------------------

// Get users by company ID (use API filtering)
export const getCompanyUsers = async (companyId: number): Promise<User[]> => {
  return getUsers({ company_id: companyId });
};

// Get users by department ID (use API filtering)
export const getDepartmentUsers = async (
  departmentId: number
): Promise<User[]> => {
  return getUsers({ department_id: departmentId });
};

// Get users by role ID (use API filtering)
export const getRoleUsers = async (roleId: number): Promise<User[]> => {
  return getUsers({ role_id: roleId });
};

// --------------------------------------------------
// UTILITY FUNCTIONS
// --------------------------------------------------

// Check if email exists
export const checkEmailExists = async (email: string): Promise<boolean> => {
  try {
    const users = await getUsers();
    return users.some(user => user.email.toLowerCase() === email.toLowerCase());
  } catch (error) {
    console.error('Error checking email existence:', error);
    return false;
  }
};

// Activate/deactivate user
export const activateUser = async (userId: number): Promise<User> => {
  return updateUser(userId, { is_active: true });
};

export const deactivateUser = async (userId: number): Promise<User> => {
  return updateUser(userId, { is_active: false });
};

// Get user statistics
export const getUserStats = async (): Promise<{
  total: number;
  active: number;
  inactive: number;
  byCompany: Record<number, number>;
  byRole: Record<number, number>;
}> => {
  const users = await getUsers();

  const stats = {
    total: users.length,
    active: users.filter(user => user.is_active).length,
    inactive: users.filter(user => !user.is_active).length,
    byCompany: {} as Record<number, number>,
    byRole: {} as Record<number, number>
  };

  users.forEach(user => {
    if (user.company_id) {
      stats.byCompany[user.company_id] =
        (stats.byCompany[user.company_id] || 0) + 1;
    }
    stats.byRole[user.role_id] = (stats.byRole[user.role_id] || 0) + 1;
  });

  return stats;
};

// Search users
export const searchUsers = async (query: string): Promise<User[]> => {
  const users = await getUsers();
  const searchTerm = query.toLowerCase().trim();

  return users.filter(
    user =>
      user.name.toLowerCase().includes(searchTerm) ||
      user.email.toLowerCase().includes(searchTerm) ||
      user.company_name?.toLowerCase().includes(searchTerm) ||
      user.department_name?.toLowerCase().includes(searchTerm) ||
      user.role_name?.toLowerCase().includes(searchTerm)
  );
};

// Bulk update users
export const bulkUpdateUsers = async (
  userIds: number[],
  updates: UpdateUserPayload
): Promise<User[]> => {
  const promises = userIds.map(userId => updateUser(userId, updates));
  return Promise.all(promises);
};

// Verify user exists
export const verifyUserExists = async (userId: number): Promise<boolean> => {
  try {
    await getUserById(userId);
    return true;
  } catch (error) {
    return false;
  }
};

// Error type for better error handling
export interface ApiError {
  message: string;
  status?: number;
  details?: unknown;
}

// Safe API call wrapper
export const safeApiCall = async <T>(
  apiCall: () => Promise<T>
): Promise<{ data: T | null; error: ApiError | null }> => {
  try {
    const data = await apiCall();
    return { data, error: null };
  } catch (error: unknown) {
    const apiError: ApiError = {
      message: 'An unexpected error occurred',
      details: error
    };

    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as {
        response?: {
          status?: number;
          data?: unknown;
        };
      };

      if (axiosError.response) {
        apiError.status = axiosError.response.status;
        apiError.message = `Request failed with status ${axiosError.response.status}`;
      }
    } else if (error instanceof Error) {
      apiError.message = error.message;
    }

    return { data: null, error: apiError };
  }
};
